import type { IDrawApprovalLine, IDrawRequest } from 'pages/Borrower/DrawRequest/type'
import { useMemo, useState } from 'react'
import { approveDrawRequest, downloadDrawApprovalPdf, voidDrawRequestApproval } from 'services'
import { Input2, Modal } from 'stories/components'
import { formatTime, getPrice2decimal, Num } from 'utils'

/**
 * What the lender will fund against a borrower's draw request, line by line.
 *
 * The step the servicing team had no screen for: after the inspection, the approved amount for
 * each line beside what was asked for, with a reason wherever it is less. The request itself is
 * never edited here - it is what the borrower attested to - so this writes a separate decision,
 * and re-approving replaces the figures while keeping the old ones in the trail.
 *
 * The amounts open on what was REQUESTED, not on zero: approving in full is the common answer, and
 * a cut should be something somebody typed rather than something a blank field did for them.
 *
 * Deliberately not a money screen. Nothing here moves anything: the draw is still entered on the
 * ledger afterwards, where Gross Eligible is the cumulative cost to date and the servicer's own
 * determination. This only says what the lender agreed to fund.
 */
export const DrawRequestApprovalModal = ({
  request,
  onClose,
  onSaved,
  onSend,
}: {
  request: IDrawRequest & { id: number }
  onClose: Function
  /** Reread the requests: the status, the totals and the budget columns all move with this. */
  onSaved: Function
  /**
   * Hand the decision to the send-to-borrower window. Only offered once a decision stands and
   * only for the figures already saved: what the borrower is sent is what was approved, not what
   * is being typed.
   */
  onSend?: Function
}) => {
  /**
   * The lines to decide on.
   *
   * `approvalLines` is the backend's own requested-beside-approved table, which is the one the
   * borrower is shown. Falling back to the raw entries keeps the screen working against a payload
   * from before it existed rather than showing an empty form.
   */
  const lines: IDrawApprovalLine[] = useMemo(() => {
    if (request?.approvalLines?.length) return request.approvalLines
    return (request?.types || []).map((entry) => ({
      value: entry.value,
      key: entry.key,
      desc: entry.desc,
      requested: Num(entry.amount),
      approved: null,
      variance: null,
      reason: '',
    }))
  }, [request])

  const lineId = (line: { value: string; key: string }) => `${line.value}-${line.key}`

  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      lines.map((line) => [
        lineId(line),
        getPrice2decimal(line.approved === null ? line.requested : line.approved, false, true),
      ]),
    ),
  )
  const [reasons, setReasons] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [lineId(line), line.reason || ''])),
  )
  const [note, setNote] = useState<string>(request?.approval?.current?.note || '')
  const [voidReason, setVoidReason] = useState<string>('')
  const [showVoid, setShowVoid] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const decided = !!request?.approval?.current
  /** Where the borrower's copy of this decision has got to, where one has been sent at all. */
  const documentation = request?.approval?.current?.documentation
  const money = (value: number) => `$${getPrice2decimal(value)}`

  const requestedTotal = useMemo(() => lines.reduce((sum, line) => sum + Num(line.requested), 0), [lines])
  const approvedTotal = useMemo(
    () => lines.reduce((sum, line) => sum + Num(amounts[lineId(line)]), 0),
    [lines, amounts],
  )

  /**
   * The same two rules the backend enforces, said here so a servicer is not told about them by a
   * rejected save: nothing negative, and a reason wherever less was approved than was asked.
   */
  const errors = useMemo(() => {
    const out: Record<string, string> = {}
    lines.forEach((line) => {
      const id = lineId(line)
      const amount = Num(amounts[id])
      if (amount < 0) out[id] = 'Cannot be negative.'
      else if (amount < Num(line.requested) && !String(reasons[id] || '').trim())
        out[id] = 'A reason is required: less than was requested.'
    })
    return out
  }, [lines, amounts, reasons])

  const onApprove = async () => {
    if (Object.keys(errors).length) return
    setLoading(true)
    try {
      await approveDrawRequest(
        request.id,
        lines.map((line) => ({
          value: line.value,
          key: line.key,
          amount: Num(amounts[lineId(line)]),
          reason: String(reasons[lineId(line)] || '').trim(),
        })),
        note,
      )
      await onSaved()
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const onVoid = async () => {
    if (!voidReason.trim()) return
    setLoading(true)
    try {
      await voidDrawRequestApproval(request.id, voidReason.trim())
      await onSaved()
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const variance = approvedTotal - requestedTotal

  return (
    <Modal
      isOpen
      title={decided ? 'Revise Draw Request Approval' : 'Approve Draw Request'}
      titleOkay={decided ? 'Save Approval' : 'Approve'}
      onOk={onApprove}
      onClose={() => onClose()}
      loading={loading}
      disabled={!!Object.keys(errors).length || !lines.length}
    >
      <div className="w-[52rem] max-w-full">
        <div className="text-[14px] mb-3">
          Requested by{' '}
          <span className="font-variation-settings-600">
            {request?.contactName || `${request?.firstName || ''} ${request?.lastName || ''}`.trim() || 'the borrower'}
          </span>
          {!!request?.createdAt && <span className="opacity-70"> on {formatTime(request.createdAt)}</span>}
        </div>

        {!!request?.note && (
          <div className="text-[13px] opacity-80 mb-3 whitespace-pre-wrap border-l-2 border-shade-blue/40 pl-3">
            {request.note}
          </div>
        )}

        <table className="w-full text-[14px]">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="px-2 py-1 border">Line</th>
              <th className="px-2 py-1 border text-right w-32">Requested</th>
              <th className="px-2 py-1 border w-40">Approved</th>
              <th className="px-2 py-1 border">Reason (required on a cut)</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const id = lineId(line)
              return (
                <tr key={id} className="align-top">
                  <td className="px-2 py-2 border">
                    <div>{line.desc || line.key}</div>
                    <div className="text-[12px] opacity-60">{line.value}</div>
                  </td>
                  <td className="px-2 py-2 border text-right whitespace-nowrap">{money(Num(line.requested))}</td>
                  <td className="px-2 py-2 border">
                    <Input2
                      type="thousandSep"
                      prefix="$"
                      value={amounts[id]}
                      error={errors[id] ? ' ' : ''}
                      onChange={(value: string) => setAmounts({ ...amounts, [id]: value })}
                    />
                  </td>
                  <td className="px-2 py-2 border">
                    <Input2
                      type="text"
                      value={reasons[id]}
                      placeholder={Num(amounts[id]) < Num(line.requested) ? 'Why less was approved' : ''}
                      error={errors[id] || ''}
                      onChange={(value: string) => setReasons({ ...reasons, [id]: value })}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className="font-variation-settings-600">
              <td className="px-2 py-2 border text-right">Total</td>
              <td className="px-2 py-2 border text-right whitespace-nowrap">{money(requestedTotal)}</td>
              <td className="px-2 py-2 border text-right whitespace-nowrap" colSpan={2}>
                {money(approvedTotal)}
                {!!variance && (
                  <span className={`ml-2 text-[13px] ${variance < 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {variance < 0 ? '-' : '+'}
                    {money(Math.abs(variance))}
                  </span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>

        <div className="mt-4">
          <Input2
            type="text"
            title="Note to the borrower (optional)"
            value={note}
            onChange={(value: string) => setNote(value)}
          />
        </div>

        {decided && (
          <div className="mt-4 text-[13px]">
            <div className="opacity-70">
              Approved by {request?.approval?.current?.by} on {formatTime(request?.approval?.current?.at || '')}
            </div>

            {/* The borrower's copy: what was requested against what was approved, with the reason
                on anything cut. Sent for acknowledgement, and filed on the loan. */}
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="text-shade-blue hover:underline"
                onClick={() =>
                  downloadDrawApprovalPdf(request.id, `Draw Approval - Request ${request.id}.pdf`)
                }
              >
                Download approval PDF
              </button>
              {!!onSend && (
                <button
                  type="button"
                  className="text-shade-blue hover:underline"
                  onClick={() => {
                    onSend(request)
                    onClose()
                  }}
                >
                  {documentation?.sentAt ? 'Resend to borrower' : 'Send to borrower'}
                </button>
              )}
              {!!documentation?.acknowledgedAt && (
                <span className="text-green-700">
                  Acknowledged by {documentation.acknowledgedBy || 'the borrower'} on{' '}
                  {formatTime(documentation.acknowledgedAt)}
                </span>
              )}
              {!documentation?.acknowledgedAt && !!documentation?.sentAt && (
                <span className="opacity-70">Sent {formatTime(documentation.sentAt)}, not yet acknowledged</span>
              )}
            </div>
            {!showVoid ? (
              <button type="button" className="text-red-700 hover:underline mt-1" onClick={() => setShowVoid(true)}>
                Void this approval
              </button>
            ) : (
              <div className="mt-2 flex items-end gap-2">
                <div className="flex-1">
                  <Input2
                    type="text"
                    title="Why the approval is being taken back"
                    value={voidReason}
                    onChange={(value: string) => setVoidReason(value)}
                  />
                </div>
                <button
                  type="button"
                  className="text-red-700 hover:underline pb-2 disabled:opacity-40 disabled:no-underline"
                  disabled={!voidReason.trim() || loading}
                  onClick={onVoid}
                >
                  Void
                </button>
                <button type="button" className="hover:underline pb-2 opacity-70" onClick={() => setShowVoid(false)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {!!request?.approval?.history?.length && (
          <div className="mt-4 text-[12px] opacity-70">
            {request.approval.history
              .slice()
              .reverse()
              .map((event, index) => (
                <div key={index}>
                  {event.action} by {event.by} on {formatTime(event.at)}
                  {!!event.reason && ` - ${event.reason}`}
                </div>
              ))}
          </div>
        )}
      </div>
    </Modal>
  )
}
